import {
  PayerPayrollScheduleDate,
  PayrollScheduleFrequency
} from "@wingspanhq/wingspan-api-sdk";
import countBy from "lodash/countBy";
import maxBy from "lodash/maxBy";
import toPairs from "lodash/toPairs";

const DAY_MS = 86400000;

export type PayrollCadence = {
  frequency: PayrollScheduleFrequency;
  /** 0 = Sunday. Only meaningful for `WEEKLY` / `BIWEEKLY`. */
  weekday: number;
};

const medianOf = (values: number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
};

const modeOf = (values: number[]) =>
  Number(maxBy(toPairs(countBy(values)), ([, count]) => count)?.[0]);

/**
 * Recovers the payer's contractor payroll cadence from the pay dates their own
 * settings already return.
 *
 * v3 has no 1099 `frequency` field, and the resource that owns cadence
 * (`/v3/payments/payroll-schedules`) answers 501 — but
 * `PayerSettings.payrollSettings.scheduleDates` serves, and those dates are the
 * cadence's own output. Inverting them is a derivation, not a proxy: unlike
 * guessing an unrelated field, a weekly schedule of Friday pay dates cannot mean
 * anything other than "weekly, on Friday".
 *
 * Two properties of real schedules shape the implementation:
 *
 * - **Individual dates shift.** A pay date landing on a weekend or holiday moves
 *   to an adjacent business day, so consecutive gaps read 6 or 8 rather than 7,
 *   and one pay date in a Friday schedule lands on a Thursday. Every decision
 *   therefore uses the median gap and the modal weekday, never the first pair,
 *   which a single shifted date would otherwise misclassify.
 * - **Semi-monthly and biweekly both average ~15 days.** They are separated by
 *   what stays fixed: a biweekly schedule repeats one weekday, a semi-monthly
 *   one repeats two days of the month.
 *
 * Returns `undefined` when there are too few dates to see an interval (fewer
 * than three), so the caller can say it does not know rather than show a guess.
 */
export const selectorPayrollCadence = (
  scheduleDates: Array<PayerPayrollScheduleDate> | undefined
): PayrollCadence | undefined => {
  const dates = (scheduleDates || [])
    .map(scheduleDate => new Date(scheduleDate.date))
    .filter(date => !isNaN(date.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());

  if (dates.length < 3) {
    return undefined;
  }

  const gaps = dates
    .slice(1)
    .map((date, index) => Math.round((+date - +dates[index]) / DAY_MS));

  const median = medianOf(gaps);
  // UTC getters, not local ones. A pay date is a calendar day the backend
  // anchors in UTC, so reading its weekday locally shifts it by one for any
  // viewer far enough from UTC — which showed a Friday schedule as Saturday.
  // Same defect, same fix as getExactCutOffDate.
  const weekday = modeOf(dates.map(date => date.getUTCDay()));

  if (median <= 2) {
    return { frequency: PayrollScheduleFrequency.DAILY, weekday };
  }

  if (median <= 10) {
    return { frequency: PayrollScheduleFrequency.WEEKLY, weekday };
  }

  if (median <= 20) {
    // Biweekly holds one weekday; semi-monthly holds two dates of the month.
    // Counting distinct days-of-month is the sharper test, because a
    // semi-monthly schedule's weekday wanders across all seven.
    const distinctDaysOfMonth = new Set(dates.map(date => date.getUTCDate()));

    return distinctDaysOfMonth.size <= 3
      ? { frequency: PayrollScheduleFrequency.SEMI_MONTHLY, weekday }
      : { frequency: PayrollScheduleFrequency.BIWEEKLY, weekday };
  }

  return { frequency: PayrollScheduleFrequency.MONTHLY, weekday };
};
